import _ from 'lodash';

// React
import * as React from "react";

// Internal components
import * as WidgetsThingsStructs from "../WidgetsThingsStructs";
import { IsNullOrEmpty, isDefined } from '@Utils/Micro';

import { AssetTrackerWidgetData } from './AssetTackerStructs';

import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';
import { IWidgetDataValidateResp } from "@UI/Widgets/IWidgetDataValidateResp";
import { Plegma } from '@API/Api';
import { WebThingPort, WebThing } from '@API/WebThing';
import { WebThingPortType } from '@API/WebNodeTypes';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import AssetTrackerUtils from './AssetTrackerUtils';
import { IWidgetThingsClass } from '../Utils/IWidgetThingsClass';

const AssetTracker = React.lazy(() => import("./AssetTrackerAsyncWidget"));
const AssetTrackerSettings = React.lazy(() => import("./AssetTrackerAsyncSettings"));

// ======================================================================================================= +++ AssetTrackerWidget
const defaultGatewayId: string = 'GW-1';
const defaultGatewayName: string = 'GW-1 Name';
// ------------------------------------------------------------------------------------------------
export class AssetTrackerWidget extends IWidgetThingsClass<AssetTrackerWidgetData> {
    // ------------------------------------------------------------------------------------------------
    Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <AssetTracker />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ------------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <AssetTrackerSettings
                key={"Settings-" + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)} // copy of the data and is return the copy in the update cb
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                gatewaysNames={{ [defaultGatewayId]: defaultGatewayName }}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>;
    }
    // ------------------------------------------------------------------------------------------------
    getPorts(): Plegma.Port[] {
        return [{
            Name: "Gateways Info",
            PortKey: "GatewaysInfo",
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            ConfFlags: Plegma.ePortConf.None,
            Description: "Gateways info",
            State: ""
        },
        {
            Name: "Assets Info",
            PortKey: "AssetsInfo",
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Input,
            ConfFlags: Plegma.ePortConf.None,
            Description: "Assets info",
            State: ""
        },
        {
            Name: "Gateway Info",
            PortKey: "GatewayInfo",
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: "The ID and name of clicked gateway",
            State: ""
        },
        {
            Name: "Asset Info",
            PortKey: "AssetInfo",
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.Output,
            ConfFlags: Plegma.ePortConf.None,
            Description: "The ID and name of clicked asset",
            State: ""
        },
        {
            Name: 'Settings',
            PortKey: 'settings',
            Type: Plegma.ePortType.JsonString,
            ioDirection: Plegma.ioPortDirection.InputOutput,
            ConfFlags: Plegma.ePortConf.None,
            Description: '',
            State: ''
        }]
    }
    // ------------------------------------------------------------------------------------------------
    GetDefaultValues(): { [key: string]: any } {
        return {
            GatewaysInfo: [
                {
                    Id: defaultGatewayId,
                    FriendlyName: defaultGatewayName,
                    Description: "GW-1 Description",
                    LocationId: 1
                }
            ],
            AssetsInfo: {
                [defaultGatewayId]: [
                    {
                        Id: "Asset-1",
                        AssetName: "Rudy's wallet",
                        ProximityLevel: "Near",
                        IsRemoved: false,
                        IsLost: true,
                        Data: "Rudy's wallet is black"
                    },
                    {
                        Id: "Asset-2",
                        AssetName: "Rudy's backpack",
                        ProximityLevel: "Immediate",
                        IsRemoved: false,
                        IsLost: false
                    },
                    {
                        Id: "Asset-3",
                        AssetName: "Ben's LED TV",
                        ProximityLevel: "Far",
                        IsRemoved: false,
                        IsLost: false
                    }
                ]
            }
        };
    }
    // ------------------------------------------------------------------------------------------------
    initThing() {
        // Thing description
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Ports
        let ports: WebThingPort[] = this.getPorts().map((port) => {
            let p = new WebThingPort(
                port,
                WebThingPortType.Data,
                (data) => this.ValidateValues(port.PortKey, data)
            );
            return p;
        });

        // Create the thing
        let thing = new WebThing(this.WebModule, plegmaThing, ports);
        this.ThingKey = thing.ThingKey;
    }
    // ------------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): AssetTrackerWidgetData {
        return _.cloneDeep({
            BackgroundImgInfo: null,
            Layout: {},
            VisibleGateways: [],
            GatewaySettings: {},
            TooltipSettings: { color: '#616161', fontSize: 14 }
        });
    }
    // ------------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        let data = AssetTrackerWidget.GetDefaultWidgetDataStatic();

        // Initialize widget's data.
        data.BackgroundImgInfo = {
            src: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAIBAQIBAQICAgICAgICAwUDAwMDAwYEBAMFBwYHBwcGBwcICQsJCAgKCAcHCg0KCgsMDAwMBwkODw0MDgsMDAz/2wBDAQICAgMDAwYDAwYMCAcIDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAz/wAARCAAMAA0DASIAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD9/KKKKAP/2Q==',
            aspectRatio: 1.5328767123287672,
            height: 730,
            width: 1119
        };
        data.Layout = AssetTrackerUtils.getDefaultCanvasLayoutDesc(defaultGatewayId);
        data.GatewaySettings = AssetTrackerUtils.getDefaultGatewaySettingsDict(defaultGatewayId);
        data.VisibleGateways = [];

        return {
            Title: "Asset Tracker",
            WidgetTitle: "",
            Key: "AssetTrackerWidget",
            Type: "AssetTrackerWidget",
            Data: data,
            Description: "Asset tracker widget",
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            // todo: find an svg icon
            IconURI: WidgetsThingsStructs.IconURIPath + "/png/asset-tracker.png"
        };
    }
    // ------------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return AssetTrackerWidget.GetDefaultWidgetDataStatic(); }
    // ------------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: AssetTrackerWidgetData): AssetTrackerWidgetData {
        return AssetTrackerUtils.migrateWidgetData(data);
    }
    // ------------------------------------------------------------------------------------------------
    ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == "GatewaysInfo")
            return AssetTrackerUtils.gatewayInfoValidator().DataValidateString(value);
        else if (key == "AssetsInfo")
            return AssetTrackerUtils.assetsInfoValidator().DataValidateString(value);
        else if (key == "settings")
            return AssetTrackerUtils.settingsValidator().DataValidateString(value);

        return {
            valid: true,
            errors: ""
        };
    };
    // ------------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            return {
                [WidgetsThingsStructs.WidgetLocalizationKeys.Title]: this.Description.WidgetTitle
            };
        else
            // nothing to expose
            return {};
    };
    // ------------------------------------------------------------------------------------------------
}
// ======================================================================================================= --- AssetTrackerWidget
